import axios from 'axios';
import { mnemosyneBaseUrl } from '../../utils/axios-interceptor';
import { buildXlsxBytes, buildXlsxFromRuntimeSheets } from './parseSheets';
import type { FortuneSheet } from './types';

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SheetStorageRef {
    type: 'S3_XLSX';
    /**
     * Either `"<mnemosyneDocId>_<versionNumber>"` (e.g. "1234_2") when the
     * bucket keeps versions, or the bare `"<mnemosyneDocId>"` (e.g. "1234")
     * when it does not — see the two storage modes below. The raw S3 versionId
     * never leaves mnemosyne; it maps sequential numbers to object versions in
     * its own doc_versions table.
     */
    docId: string;
}

/**
 * Runtime guard for storage refs arriving from the document API (untyped).
 * Every field the READ path depends on must be present and well-formed — a
 * partial ref must be rejected here (falling back to inline content) rather
 * than surfacing as a confusing mnemosyne error. Deliberately NO back-compat
 * for the old `{s3Path,bucket,fileName,sizeKb}` shape — it never reached prod,
 * so an old dev/staging row just falls back to content-less (acceptable).
 * Mirrors the documented contract on aica's OnePager.storage / DocumentRequest.storage.
 */
export const isSheetStorageRef = (value: unknown): value is SheetStorageRef => {
    if (value == null || typeof value !== 'object') return false;
    const ref = value as Partial<SheetStorageRef>;
    return ref.type === 'S3_XLSX' && typeof ref.docId === 'string' && STORAGE_DOC_ID.test(ref.docId);
};

/**
 * `"<numericDocId>"` or `"<numericDocId>_<versionNumber>"` — all numeric.
 *
 * The two forms ARE the two storage modes. A versioned bucket answers a PUT
 * with `x-amz-version-id`, so every save is registered as a numbered version and
 * the ref carries that number. A bucket without versioning (or whose CORS does
 * not expose the header) has no version to name: the save simply overwrites the
 * one object, the ref stays bare, and reads resolve to whatever is current.
 */
const STORAGE_DOC_ID = /^\d+(_\d+)?$/;

/**
 * THE canonical processors for the storage docId. Any code that receives a
 * `storage.docId` (document rows, history entries) must go through these —
 * never ad-hoc string splitting — so the format has exactly one owner.
 *
 * A null/undefined `versionNumber` composes the bare form, which is how an
 * unversioned bucket's saves are addressed.
 */
export const composeVersionedDocId = (fileId: number, versionNumber?: number | null): string =>
    versionNumber == null ? `${fileId}` : `${fileId}_${versionNumber}`;

export const parseVersionedDocId = (docId: string): { fileId: number; version?: number } => {
    if (typeof docId !== 'string' || !STORAGE_DOC_ID.test(docId)) {
        throw new Error(`Malformed storage docId "${docId}" — expected "<docId>" or "<docId>_<versionNumber>"`);
    }
    const [fileId, version] = docId.split('_').map(Number);
    return version == null || Number.isNaN(version) ? { fileId } : { fileId, version };
};

/** True when this ref names a specific version — i.e. the lineage has history. */
export const hasVersionHistory = (ref?: SheetStorageRef | null): boolean =>
    ref != null && typeof ref.docId === 'string' && ref.docId.includes('_');

// ---------------------------------------------------------------------------
// Mnemosyne category + flow (hardcoded constants — the app convention)
// ---------------------------------------------------------------------------

// Report sheets reuse the editor's existing mnemosyne category + flow (same as
// PlateEditor image uploads) — app-internal editor blobs, invisible to Data Room
// and Magic Upload via null docType + no babler registration. No provisioning
// needed; 100649/16 exist in every environment.
const REPORT_SHEETS_CATEGORY_ID = 100649;
const REPORT_SHEETS_FLOW_ID = 16;

// ---------------------------------------------------------------------------
// Feature kill-switch
// ---------------------------------------------------------------------------

// Master switch for S3-backed sheet storage. Ship-ordering safety: mnemosyne
// (v2/saveMetadata version-register mode + `version` on `downloadFilePresigned`) must
// deploy BEFORE aica #1794, which must deploy BEFORE this flag goes live — else
// sheet saves would try to persist a storage ref the backend can't yet resolve.
// Flip to false to fully disable and fall back to legacy inline save everywhere.
const SHEET_S3_STORAGE_ENABLED = true;
export const isSheetS3StorageEnabled = (): boolean => SHEET_S3_STORAGE_ENABLED;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** 8-char hex uid — minted once per document lineage (one S3 key for its whole life). */
const mintUid8 = (): string => Math.random().toString(16).slice(2, 10).padEnd(8, '0');

// ---------------------------------------------------------------------------
// Mnemosyne service calls (thin, purpose-built for sheet storage)
// ---------------------------------------------------------------------------

interface SaveMetadataInput {
    categoryId: number;
    fileName: string;
    fileSizeKb: number;
    fileType: string;
    flowId: number;
    mnemosyneOrgId?: string;
}

const saveFileMetadata = async (input: SaveMetadataInput, orgId: string): Promise<{ docId: number; fileName?: string }> => {
    const res = await axios({
        method: 'POST',
        url: `${mnemosyneBaseUrl}/file/saveMetadata`,
        data: input,
        headers: { 'organization-id': orgId },
    });
    const data = res?.data;
    if (data?.responseData?.responseCode !== 'OK') {
        throw new Error(data?.responseData?.responseMessage ?? 'Failed to save file metadata');
    }
    const docId = data?.fileMetadata?.id;
    if (docId == null) {
        throw new Error('saveMetadata response missing fileMetadata.id');
    }
    // The server may clean the fileName it stores — the STORED name is what the
    // S3 key derives from, so it's the one every later presign must reuse.
    return { docId, fileName: data?.fileMetadata?.fileName };
};

/** Presigned PUT URL for the lineage's single S3 key — the EXISTING /file/signedUrl
 *  flow. Key derivation is deterministic from (org, category, fileName), so the same
 *  fileName always presigns the same key every version. */
const fetchPresignedUploadUrl = async (
    input: { fileName: string; fileSizeKb: number; fileType: string; categoryId: number; mnemosyneOrgId?: string },
    orgId: string,
): Promise<string> => {
    const res = await axios({
        method: 'POST',
        url: `${mnemosyneBaseUrl}/file/signedUrl`,
        data: input,
        headers: { 'organization-id': orgId },
    });
    const data = res?.data;
    if (data?.responseData?.responseCode !== 'OK' || !data?.signedUrl) {
        throw new Error(data?.responseData?.responseMessage ?? 'Failed to get presigned URL');
    }
    return data.signedUrl;
};

/**
 * PUT the bytes and report the S3 object version the write created, when there
 * is one.
 *
 * A versioning-enabled bucket answers with `x-amz-version-id`, and that id is
 * the write's identity: mnemosyne records it verbatim, so a register can never
 * attribute another writer's bytes to this save (the RECUR-27628 race).
 *
 * Undefined is a NORMAL outcome, not a failure — it means this bucket keeps no
 * versions (versioning off), or the browser cannot read the header because the
 * bucket's CORS policy does not list it under `ExposeHeaders` (it is not
 * CORS-safelisted). Either way the object was written; the save simply proceeds
 * without history. This is what makes the feature work on every bucket with no
 * infrastructure prerequisite.
 */
const putBytesToS3 = async (signedUrl: string, bytes: ArrayBuffer): Promise<string | undefined> => {
    const response = await fetch(signedUrl, {
        method: 'PUT',
        body: bytes,
        headers: new Headers({ 'Content-Type': XLSX_MIME }),
    });
    if (!response.ok) {
        throw new Error(`S3 PUT failed: ${response.status} ${response.statusText}`);
    }
    // Optional-chained: a mocked//minimal Response may carry no headers at all.
    // "null" is what a version-SUSPENDED bucket reports — no more useful than a
    // missing header, so it degrades the same way.
    const versionId = response.headers?.get?.('x-amz-version-id');
    return versionId && versionId !== 'null' ? versionId : undefined;
};

/** The lineage fileName is minted once and never changes — cache it per docId so
 *  subsequent saves don't refetch metadata. */
const docIdFileNameCache = new Map<number, string>();

const resolveFileName = async (docId: number): Promise<string> => {
    const cached = docIdFileNameCache.get(docId);
    if (cached) return cached;
    const res = await axios({ method: 'POST', url: `${mnemosyneBaseUrl}/file/get-file-meta-data?fileId=${docId}` });
    const fileName = res?.data?.docMetaData?.fileName;
    if (!fileName) {
        throw new Error(res?.data?.responseData?.responseMessage ?? 'Failed to resolve file metadata');
    }
    docIdFileNameCache.set(docId, fileName);
    return fileName;
};

/**
 * Register the just-PUT bytes as a version of the doc — the EXISTING JSON
 * `POST /file/v2/saveMetadata` with `id` set (version-register mode). mnemosyne
 * records the versionId in its doc_versions table and answers with the sequential
 * versionNumber on the fileMetadata; raw S3 ids are never persisted on this side.
 *
 * Draft lifecycle mirrors the aica document row: `isDraft: true` (autosave
 * tick) re-points the lineage's single live draft version in place — SAME
 * versionNumber; `isDraft: false` (Save & Close) finalizes it. History grows one
 * version per commit, however many autosave ticks preceded it.
 *
 * `fileSizeKb` is sent because mnemosyne does no S3 round-trip of its own on this
 * call: the caller already knows the size, so a HEAD to rediscover it would be a
 * wasted request on every autosave tick. Sending the SAME sizeKb the
 * create/presign calls sent also keeps mnemosyne's name+size+type checksum
 * consistent across the lineage.
 *
 * `versionId` is OPTIONAL. With one, mnemosyne records a numbered version and
 * answers with its `versionNumber`. Without one (unversioned bucket, or the
 * header not exposed to the browser) it records no version, answers with none,
 * and this returns undefined — the caller then stores a version-less ref and the
 * document simply has no history.
 */
const registerVersion = async (
    docId: number,
    orgId: string,
    isDraft: boolean,
    fileSizeKb: number,
    versionId?: string,
): Promise<number | undefined> => {
    const res = await axios({
        method: 'POST',
        url: `${mnemosyneBaseUrl}/file/v2/saveMetadata`,
        data: { id: docId, orgId, isDraft, fileSizeKb, ...(versionId ? { versionId } : {}) },
        headers: { 'organization-id': orgId },
    });
    const data = res?.data;
    if (data?.responseData?.responseCode !== 'OK') {
        throw new Error(data?.responseData?.responseMessage ?? 'Failed to register file version');
    }
    // Absent versionNumber is expected in the unversioned mode; only a mismatch
    // (we sent a versionId but got no number back) is a real failure.
    const versionNumber = data?.fileMetadata?.versionNumber;
    if (versionId && versionNumber == null) {
        throw new Error('Version register did not return a versionNumber for a versioned upload');
    }
    return versionNumber ?? undefined;
};

/**
 * Get a presigned download URL for a stored file via mnemosyne's
 * `downloadFilePresigned` (query params; the server reads them as
 * `@RequestParam`). `version` is the sequential number, which mnemosyne resolves
 * to an S3 object version internally; omitting it (the unversioned mode, where
 * no number exists) resolves to whatever the key currently holds.
 */
const fetchDownloadUrl = async (fileId: number, version?: number): Promise<string> => {
    const res = await axios({
        method: 'POST',
        url: `${mnemosyneBaseUrl}/file/downloadFilePresigned?fileId=${fileId}` + (version == null ? '' : `&version=${version}`),
    });
    const data = res?.data;
    if (!data?.signedUrl) {
        throw new Error(data?.responseData?.responseMessage ?? 'Failed to get download URL');
    }
    return data.signedUrl;
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface UploadSheetVersionArgs {
    /** Required unless `rawBytes` is given. */
    sheets?: FortuneSheet[];
    /** Already-xlsx bytes (e.g. a freshly-imported .xlsx file) — uploaded
     *  as-is, skipping serialization entirely. Takes precedence over `sheets`. */
    rawBytes?: ArrayBuffer;
    assesseeOrgId: string;
    investorOrgId?: string;
    existing?: SheetStorageRef;
    /** `sheets` is RUNTIME shape (2D `data`, straight from `getAllSheets()`) —
     *  serialize via the combined worker job (toCelldataSheet + buildXlsxBuffer
     *  in one round trip) instead of assuming celldata form is already built. */
    runtimeSheets?: boolean;
    /** Autosave tick: re-points the lineage's live draft version in place
     *  instead of finalizing it — see registerVersion. Absent/false = commit. */
    isDraft?: boolean;
}

/**
 * Serialize sheets to xlsx (or reuse already-xlsx `rawBytes` as-is) and PUT the
 * bytes onto the lineage's single mnemosyne-owned S3 key. Mints a mnemosyne
 * docId (and its one-time filename) only on the first save; every later save
 * reuses `existing.docId` and PUTs the same key again. Returns the storage ref
 * the caller persists on the aica document row.
 *
 * Works on any bucket. Where S3 keeps object versions, each save is registered
 * as a numbered version and the returned ref names it, giving the document
 * history. Where it does not, the PUT simply overwrites and the ref is
 * version-less — the document has a single, current version and no history.
 * The bucket decides; nothing here needs configuring.
 */
export const uploadSheetVersion = async ({
    sheets,
    rawBytes,
    assesseeOrgId,
    investorOrgId,
    existing,
    runtimeSheets,
    isDraft,
}: UploadSheetVersionArgs): Promise<SheetStorageRef> => {
    if (!rawBytes && !sheets) {
        throw new Error('uploadSheetVersion requires either sheets or rawBytes');
    }
    // 1. Get xlsx bytes — an import's rawBytes ARE already the xlsx we persist
    // (skip serializing entirely); otherwise serialize `sheets`.
    const bytes =
        rawBytes ??
        (runtimeSheets ? await buildXlsxFromRuntimeSheets(sheets as FortuneSheet[]) : await buildXlsxBytes(sheets as FortuneSheet[]));

    // 2. First save: mint a mnemosyne docId (+ its one-time fileName) via
    // saveMetadata. Later saves reuse it — the fileName never changes again, which
    // is what keeps every presigned PUT landing on the SAME S3 key.
    const sizeKb = Math.round(bytes.byteLength / 1000);
    // No `existing` means a first save, which mints. A malformed `existing` is a
    // different thing entirely and must THROW: swallowing it would mint a fresh
    // lineage, orphaning the document's current S3 object and silently forking it
    // where the user expected an update. isSheetStorageRef already rejects
    // malformed refs at the read boundary, so one arriving here is a bug to
    // surface, not to route around.
    let docId = existing?.docId ? parseVersionedDocId(existing.docId).fileId : undefined;
    let fileName: string;
    if (docId == null) {
        const minted = await saveFileMetadata(
            {
                categoryId: REPORT_SHEETS_CATEGORY_ID,
                fileName: `sheet_${mintUid8()}.xlsx`,
                fileSizeKb: sizeKb,
                fileType: XLSX_MIME,
                flowId: REPORT_SHEETS_FLOW_ID,
                ...(investorOrgId ? { mnemosyneOrgId: investorOrgId } : {}),
            },
            assesseeOrgId,
        );
        docId = minted.docId;
        // Prefer the SERVER's stored name — the S3 key derives from what the
        // Docs row holds, and the server may clean the name it was sent.
        fileName = minted.fileName ?? (await resolveFileName(docId));
        docIdFileNameCache.set(docId, fileName);
    } else {
        fileName = await resolveFileName(docId);
    }

    // 3. PUT the bytes onto the lineage's single key via the existing presigned-PUT
    // flow, then register the new version via the existing /v2/saveMetadata (id set).
    const signedUrl = await fetchPresignedUploadUrl(
        {
            fileName,
            fileSizeKb: sizeKb,
            fileType: XLSX_MIME,
            categoryId: REPORT_SHEETS_CATEGORY_ID,
            ...(investorOrgId ? { mnemosyneOrgId: investorOrgId } : {}),
        },
        assesseeOrgId,
    );
    // The bucket answers with a versionId only when it keeps versions; that single
    // fact selects the mode for this save (numbered version, or plain overwrite).
    const s3VersionId = await putBytesToS3(signedUrl, bytes);
    const versionNumber = await registerVersion(docId, assesseeOrgId, isDraft === true, sizeKb, s3VersionId);

    return { type: 'S3_XLSX', docId: composeVersionedDocId(docId, versionNumber) };
};

/**
 * Get a presigned download URL for a stored sheet. A ref naming a version
 * resolves to exactly those bytes; a version-less ref (unversioned bucket)
 * resolves to whatever the key currently holds.
 */
export const getSheetDownloadUrl = async (ref: SheetStorageRef): Promise<string> => {
    if (!ref?.docId) {
        throw new Error('Cannot download a sheet without a storage docId');
    }
    const { fileId, version } = parseVersionedDocId(ref.docId);
    return fetchDownloadUrl(fileId, version);
};
